import React, { useState, useEffect } from 'react';
import { FaSync, FaSearch, FaEye, FaPlay, FaCheck, FaTimes, FaPaperPlane, FaPaperclip, FaDownload, FaComments, FaBuilding, FaUser, FaTicketAlt, FaCog } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import apiClient from '../../../api/apiClient';
import { Modal, Row, Col, Button, Spinner } from 'react-bootstrap';

const ManageSupportTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    organization_id: ''
  });

  const [messageForm, setMessageForm] = useState({
    message: '',
    is_internal: false
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const statusColors = {
    open: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-cyan-100 text-cyan-800',
    resolved: 'bg-emerald-100 text-emerald-800',
    closed: 'bg-gray-100 text-gray-700'
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchOrganizations();
  }, [filters, activeTab]);

  const fetchTickets = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15',
        ...filters
      });

      const endpoint = activeTab === 'all' ? '/support-tickets/admin/all' : '/support-tickets';
      const response = await apiClient.get(`${endpoint}?${params}`);
      
      if (response.data.success) {
        setTickets(response.data.data.tickets);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/support-tickets/admin/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await apiClient.get('/orgs');
      if (response.data.success) {
        setOrganizations(response.data.data.organizations || []);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/support-tickets/${ticketId}`);
      
      if (response.data.success) {
        setSelectedTicket(response.data.data);
        setShowTicketModal(true);
        setMessageForm({ message: '', is_internal: false });
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      setError('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.message.trim() && !selectedFile) return;

    try {
      setLoading(true);
      
      if (messageForm.message.trim()) {
        const messageResponse = await apiClient.post(`/support-tickets/${selectedTicket.id}/messages`, {
          message: messageForm.message.trim(),
          is_internal: messageForm.is_internal
        });
        
        if (!messageResponse.data.success) {
          throw new Error('Failed to send message');
        }
      }

      if (selectedFile) {
        const formData = new FormData();
        formData.append('attachment', selectedFile);
        
        const attachmentResponse = await apiClient.post(`/support-tickets/${selectedTicket.id}/attachments`, formData);
        
        if (!attachmentResponse.data.success) {
          throw new Error('Failed to upload attachment');
        }
      }

      const refreshResponse = await apiClient.get(`/support-tickets/${selectedTicket.id}`);
      if (refreshResponse.data.success) {
        setSelectedTicket(refreshResponse.data.data);
      }

      setMessageForm({ message: '', is_internal: false });
      setSelectedFile(null);
      setSuccess('Message sent successfully');

    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      await apiClient.supportTickets.downloadAttachment(attachmentId);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download attachment. Please try again.');
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus, resolutionNotes = '') => {
    try {
      const response = await apiClient.patch(`/support-tickets/${ticketId}/status`, {
        status: newStatus,
        resolution_notes: resolutionNotes
      });
      
      if (response.data.success) {
        setSuccess(`Ticket status updated to ${newStatus.replace('_', ' ')}`);
        fetchTickets();
        fetchStats();
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      setError(error.response?.data?.message || 'Failed to update ticket status');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredTickets = () => {
    switch (activeTab) {
      case 'organization':
        return tickets.filter(ticket => ticket.is_organization_ticket);
      case 'user':
        return tickets.filter(ticket => !ticket.is_organization_ticket && ticket.organization_id);
      case 'independent':
        return tickets.filter(ticket => !ticket.is_organization_ticket && !ticket.organization_id);
      default:
        return tickets;
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h4 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Support Tickets
        </h4>
        <button
          onClick={() => fetchTickets()}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-2 bg-white border-2 border-purple-400 text-purple-700 rounded-xl shadow-lg hover:bg-purple-50 transition-all"
        >
          <FaSync className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border-2 border-red-400 text-red-800 rounded-xl">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-emerald-100 border-2 border-emerald-400 text-emerald-800 rounded-xl">
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-purple-200 p-6 text-center">
          <div className="text-4xl font-bold text-purple-600">{stats.total || 0}</div>
          <div className="text-gray-600 mt-2">Total Tickets</div>
        </div>
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-blue-200 p-6 text-center">
          <div className="text-4xl font-bold text-blue-600">{stats.open || 0}</div>
          <div className="text-gray-600 mt-2">Open</div>
        </div>
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-cyan-200 p-6 text-center">
          <div className="text-4xl font-bold text-cyan-600">{stats.in_progress || 0}</div>
          <div className="text-gray-600 mt-2">In Progress</div>
        </div>
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-emerald-200 p-6 text-center">
          <div className="text-4xl font-bold text-emerald-600">{stats.resolved || 0}</div>
          <div className="text-gray-600 mt-2">Resolved</div>
        </div>
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-gray-300 p-6 text-center">
          <div className="text-4xl font-bold text-gray-600">{stats.closed || 0}</div>
          <div className="text-gray-600 mt-2">Closed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Organization</label>
            <select
              value={filters.organization_id}
              onChange={(e) => handleFilterChange('organization_id', e.target.value)}
              className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
            >
              <option value="">All Organizations</option>
              <option value="null">Independent Users</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Search</label>
            <div className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden input-group border">
                <span className="bg-white border-0 text-purple-600 px-3 input-group-text">
                  <FaSearch className="w-4 h-4" />
                </span>
            <input
              placeholder="Search by subject, description, or ticket number..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base form-control"
            />
            </div>
          
          </div>
        </div>
      </div>

      {/* Tabs & Table */}
      <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-purple-200 overflow-hidden">
        <div className="border-b border-purple-200">
          <div className="flex flex-wrap gap-2 p-4">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${activeTab === 'all' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              All Tickets ({stats.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('organization')}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${activeTab === 'organization' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Organization Tickets
            </button>
            <button
              onClick={() => setActiveTab('user')}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${activeTab === 'user' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              User Tickets
            </button>
            <button
              onClick={() => setActiveTab('independent')}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${activeTab === 'independent' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Independent Users
            </button>
          </div>
        </div>

        <div className="">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-8 border-purple-500 border-t-transparent"></div>
              <p className="mt-6 text-2xl text-purple-700 font-bold">Loading tickets...</p>
            </div>
          ) : getFilteredTickets().length === 0 ? (
            <div className="text-center py-20">
              <FaTicketAlt className="w-20 h-20 text-purple-300 mx-auto mb-6 opacity-50" />
              <h5 className="text-2xl font-bold text-purple-700 mb-3">No tickets found</h5>
              <p className="text-lg text-gray-600">No tickets match the current filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-4 px-6 text-left font-bold">Ticket #</th>
                    <th className="py-4 px-6 text-left font-bold">Subject</th>
                    <th className="py-4 px-6 text-left font-bold">Created By</th>
                    <th className="py-4 px-6 text-left font-bold">Organization</th>
                    <th className="py-4 px-6 text-center font-bold">Status</th>
                    <th className="py-4 px-6 text-left font-bold">Created</th>
                    <th className="py-4 px-6 text-center font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredTickets().map((ticket) => (
                    <tr key={ticket.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                      <td className="py-4 px-6">
                        <code>{ticket.ticket_number}</code>
                        {ticket.is_organization_ticket && (
                          <span className="ml-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs">ORG</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-grey-800">{ticket.subject}</div>
                        <div className="text-gray-800 text-xs mt-1">
                          {ticket.description.substring(0, 60)}
                          {ticket.description.length > 60 && '...'}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div >{ticket.creator.first_name} {ticket.creator.last_name}</div>
                        <div className="text-gray-800 text-xs">{ticket.creator.email}</div>
                        {ticket.creator.role === 2 && <span className="inline-block mt-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold">Org Admin</span>}
                      </td>
                      <td className="py-4 px-6">
                        {ticket.organization ? (
                          <div className="text-gray-800">{ticket.organization.name}</div>
                        ) : (
                          <span className="bg-gray-200 text-gray-700 px-4 py-2 rounded-full text-xs font-bold">Independent</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-xs ${statusColors[ticket.status]}`}>
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-700">
                        {formatDate(ticket.createdAt)}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={() => handleViewTicket(ticket.id)}
                            className="text-purple-600 hover:bg-purple-100 p-3 rounded-xl transition-all"
                            title="View Details"
                          >
                            <FaEye className="w-5 h-5" />
                          </button>
                          {ticket.status === 'open' && (
                            <button
                              onClick={() => handleUpdateStatus(ticket.id, 'in_progress')}
                              className="text-cyan-600 hover:bg-cyan-100 p-3 rounded-xl transition-all"
                              title="Mark In Progress"
                            >
                              <FaPlay className="w-5 h-5" />
                            </button>
                          )}
                          {ticket.status === 'in_progress' && (
                            <button
                              onClick={() => handleUpdateStatus(ticket.id, 'resolved')}
                              className="text-emerald-600 hover:bg-emerald-100 p-3 rounded-xl transition-all"
                              title="Mark Resolved"
                            >
                              <FaCheck className="w-5 h-5" />
                            </button>
                          )}
                          {ticket.status === 'resolved' && (
                            <button
                              onClick={() => handleUpdateStatus(ticket.id, 'closed')}
                              className="text-gray-800 hover:bg-gray-200 p-3 rounded-xl transition-all"
                              title="Close Ticket"
                            >
                              <FaTimes className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-6 mt-8">
              <button
                disabled={pagination.page === 1}
                onClick={() => fetchTickets(pagination.page - 1)}
                className="px-6 py-3 bg-white border-2 border-purple-400 text-purple-700 rounded-xl shadow-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
              >
                Previous
              </button>
              <span className="text-xl font-bold text-purple-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                disabled={pagination.page === pagination.totalPages}
                onClick={() => fetchTickets(pagination.page + 1)}
                className="px-6 py-3 bg-white border-2 border-purple-400 text-purple-700 rounded-xl shadow-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ticket Detail Modal (react-bootstrap pattern) */}
      <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} centered size="lg" backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 rounded-top">
          <Modal.Title>
            <div className="d-flex align-items-center gap-3">
              <code className="bg-white/20 px-3 py-1 rounded">{selectedTicket?.ticket_number}</code>
              <span className="font-extrabold">{selectedTicket?.subject}</span>
              {selectedTicket?.is_organization_ticket && (
                <span className="ml-3 bg-blue-600 px-3 py-1 rounded-full text-sm font-bold">Organization Ticket</span>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <div className="p-0">
            <div className="p-0">
              <Row>
                <Col lg={8}>
                  {/* Ticket Details */}
                  <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-4 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="text-xl font-bold text-purple-800">Ticket Details</h5>
                      <div className="d-flex gap-2">
                        {selectedTicket?.status === 'open' && (
                          <Button variant="primary" className="bg-cyan-600 border-0" onClick={() => handleUpdateStatus(selectedTicket.id, 'in_progress')}>
                             
                          <div className="d-flex align-items-center gap-2 mt-1"><FaPlay/>Mark In Progress</div>

                          </Button>
                        )}
                        {selectedTicket?.status === 'in_progress' && (
                          <Button variant="success" onClick={() => handleUpdateStatus(selectedTicket.id, 'resolved')}>
                            <div className="d-flex align-items-center gap-2 mt-1"><FaCheck /> Mark Resolved</div>

                            
                          </Button>
                        )}
                        {selectedTicket?.status === 'resolved' && (
                          <Button variant="secondary" onClick={() => handleUpdateStatus(selectedTicket.id, 'closed')}>
                            <div className="d-flex align-items-center gap-2 mt-1"><FaTimes /> Close Ticket</div>

                            
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-700">{selectedTicket?.description}</p>
                  </div>

                  {/* Messages */}
                  <div className="bg-white rounded-2xl shadow-sm border border-purple-200 mb-4">
                    <div className="p-3 border-bottom">
                      <h5 className="text-xl font-bold text-purple-800">Conversation ({selectedTicket?.messages?.length || 0})</h5>
                    </div>
                    <div className="max-h-96 overflow-y-auto p-3">
                      {selectedTicket?.messages && selectedTicket.messages.length > 0 ? (
                        selectedTicket.messages.map((message) => (
                          <div key={message.id} className={`d-flex ${message.sender.id === user.id ? 'justify-content-end' : 'justify-content-start'} mb-3`}>
                            <div className={`p-3 rounded-2xl ${message.sender.id === user.id ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                              <div className="font-bold d-flex align-items-center gap-2">
                                {message.sender.first_name} {message.sender.last_name}
                                {message.sender.role === 0 && <span className="bg-red-600 px-2 py-1 rounded-full text-xs">Super Admin</span>}
                                {message.sender.role === 2 && <span className="bg-blue-600 px-2 py-1 rounded-full text-xs">Org Admin</span>}
                                {message.is_internal && <span className="bg-yellow-500 px-2 py-1 rounded-full text-xs">Internal</span>}
                              </div>
                              <p className="mt-2 whitespace-pre-wrap">{message.message}</p>
                              <div className="text-xs mt-2 opacity-80">{formatDate(message.createdAt)}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <FaComments className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No messages yet. Start the conversation!</p>
                        </div>
                      )}
                    </div>

                    {selectedTicket?.status !== 'closed' && (
                      <div className="p-3 bg-gray-50 border-top">
                        <form onSubmit={handleSendMessage}>
                          <div className="mb-3">
                            <label className="d-flex align-items-center gap-2 text-purple-700 font-bold">
                              <input type="checkbox" checked={messageForm.is_internal} onChange={(e) => setMessageForm(prev => ({ ...prev, is_internal: e.target.checked }))} className="me-2" />
                              Internal message (only visible to admins)
                            </label>
                          </div>
                          <div className="d-flex gap-3">
                            <textarea rows={3} placeholder="Type your response..." value={messageForm.message} onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))} className="flex-1 px-3 py-2 rounded border" required />
                            <div className="d-flex flex-column gap-2">
                              <Button type="submit" disabled={loading || !messageForm.message.trim()} className="bg-gradient-to-r from-purple-600 to-pink-600 border-0 p-2">
                                <FaPaperPlane />
                              </Button>
                              <label className="bg-gray-200 hover:bg-gray-300 p-2 rounded cursor-pointer text-center">
                                <FaPaperclip className="w-5 h-5 text-gray-700" />
                                <input id="admin-attachment-input" type="file" className="d-none" onChange={handleFileSelect} accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt" />
                              </label>
                            </div>
                          </div>

                          {selectedFile && (
                            <div className="mt-3 p-2 bg-purple-50 rounded d-flex justify-content-between align-items-center">
                              <span className="d-flex align-items-center gap-2"><FaPaperclip /> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                              <button type="button" onClick={() => setSelectedFile(null)} className="text-danger btn btn-link"> <FaTimes /> </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Sidebar */}
                <Col lg={4} className="space-y-3">
                  <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-3">
                    <h5 className="text-xl font-bold text-purple-800 mb-3">Ticket Summary</h5>
                    <div className="space-y-3 text-gray-700 small">
                      <div>
                        <strong>Origin:</strong>
                        {selectedTicket?.organization ? (
                          <div className="d-flex align-items-center gap-2 mt-1"><FaBuilding className="text-purple-600" />{selectedTicket.organization.name}</div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 mt-1"><FaUser className="text-gray-600" />Independent User</div>
                        )}
                      </div>
                      <div>
                        <strong>Type:</strong>
                        <div className="mt-1">
                          {selectedTicket?.is_organization_ticket ? (
                            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">Organization Level</span>
                          ) : (
                            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full font-bold">User Level</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <strong>Last Updated:</strong>
                        <div className="mt-1">{selectedTicket ? formatDate(selectedTicket.updatedAt) : ''}</div>
                      </div>
                    </div>
                  </div>

                  {selectedTicket?.attachments && selectedTicket.attachments.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-purple-200 p-3">
                      <h5 className="text-xl font-bold text-purple-800 mb-3">Attachments ({selectedTicket.attachments.length})</h5>
                      <div className="space-y-2">
                        {selectedTicket.attachments.map((attachment) => (
                          <div key={attachment.id} className="d-flex justify-content-between align-items-center p-2 bg-gray-50 rounded">
                            <div>
                              <div >{attachment.original_filename}</div>
                              <div className="text-xs text-gray-800">{(attachment.file_size / 1024).toFixed(1)} KB • {attachment.uploader.first_name} {attachment.uploader.last_name}</div>
                            </div>
                            <button onClick={() => handleDownloadAttachment(attachment.id)} className="text-purple-600 btn btn-link"><FaDownload /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-gray-100 border-0">
          <div className="w-100 d-flex justify-content-end">
            <Button variant="light" onClick={() => setShowTicketModal(false)} className="px-4 py-2 rounded">Close</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageSupportTickets;